import { DEFAULT_LOCALE, getPathLocale, SUPPORTED_LOCALES } from "@/services/locale.service";

const SITE_URL = "https://suarte.art";

function normalizeLocale(locale) {
  if (typeof locale !== "string") {
    return DEFAULT_LOCALE;
  }

  const normalized = locale.trim().toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
}

function stripLocalePrefix(pathname = "/") {
  const normalizedPath = typeof pathname === "string" && pathname.trim() ? pathname.trim() : "/";
  const pathLocale = getPathLocale(normalizedPath);
  if (!pathLocale || pathLocale === DEFAULT_LOCALE) {
    return normalizedPath;
  }

  const prefix = `/${pathLocale}`;
  if (normalizedPath === prefix) {
    return "/";
  }

  if (normalizedPath.startsWith(`${prefix}/`)) {
    return normalizedPath.slice(prefix.length) || "/";
  }

  return normalizedPath;
}

export function buildLocalizedPublicPath(pathname = "/", locale = DEFAULT_LOCALE) {
  const normalizedLocale = normalizeLocale(locale);
  const strippedPath = stripLocalePrefix(pathname);
  if (normalizedLocale === DEFAULT_LOCALE) {
    return strippedPath;
  }

  return strippedPath === "/" ? `/${normalizedLocale}` : `/${normalizedLocale}${strippedPath}`;
}

export function resolveCurrentPathLocale() {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  return getPathLocale(window.location.pathname) || DEFAULT_LOCALE;
}

export function resolveLocalizedPublicPath(pathname = "/", locale) {
  const currentLocale = normalizeLocale(locale || resolveCurrentPathLocale());
  return buildLocalizedPublicPath(pathname, currentLocale);
}

export function assignLocalizedPublicPath(pathname = "/", options = {}) {
  const nextPath = resolveLocalizedPublicPath(pathname, options.locale);
  if (typeof window !== "undefined") {
    if (options.replace) {
      window.location.replace(nextPath);
    } else {
      window.location.assign(nextPath);
    }
  }
  return nextPath;
}

export function resolveLocalizedCanonicalUrl({ canonicalUrl, canonicalPath, locale }) {
  const currentLocale = normalizeLocale(locale || resolveCurrentPathLocale());

  if (typeof canonicalUrl === "string" && canonicalUrl.trim()) {
    try {
      const parsed = new URL(canonicalUrl.trim());
      if (parsed.origin !== SITE_URL) {
        return parsed.toString();
      }

      return `${SITE_URL}${buildLocalizedPublicPath(parsed.pathname, currentLocale)}`;
    } catch (_error) {
      return canonicalUrl.trim();
    }
  }

  if (typeof canonicalPath === "string" && canonicalPath.trim()) {
    const normalizedPath = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
    return `${SITE_URL}${buildLocalizedPublicPath(normalizedPath, currentLocale)}`;
  }

  return `${SITE_URL}${buildLocalizedPublicPath("/", currentLocale)}`;
}

export function buildLocalizedAlternateUrls({ canonicalUrl, canonicalPath, locale }) {
  const resolvedCanonicalUrl = resolveLocalizedCanonicalUrl({ canonicalUrl, canonicalPath, locale });

  try {
    const parsed = new URL(resolvedCanonicalUrl);
    if (parsed.origin !== SITE_URL) {
      return [];
    }

    const strippedPath = stripLocalePrefix(parsed.pathname);
    return SUPPORTED_LOCALES.map((supportedLocale) => ({
      hrefLang: supportedLocale,
      href: `${SITE_URL}${buildLocalizedPublicPath(strippedPath, supportedLocale)}`,
    })).concat([{ hrefLang: "x-default", href: `${SITE_URL}${buildLocalizedPublicPath(strippedPath, DEFAULT_LOCALE)}` }]);
  } catch (_error) {
    return [];
  }
}
